"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.TokenExchangeResponseSchema = exports.TokenExchangeRequestSchema = exports.VaultConnectSchema = exports.VaultProviderInfoSchema = exports.CreateDelegationSchema = exports.DelegationGrantSchema = exports.DelegationConstraintsSchema = exports.DelegationGrantStatusEnum = exports.UpdateAgentSchema = exports.CreateAgentSchema = exports.AgentIdentitySchema = exports.AgentStatusEnum = void 0;
const zod_1 = require("zod");
// ── Agent Identity ──────────────────────────────────────────────────
exports.AgentStatusEnum = zod_1.z.enum(["active", "suspended", "revoked"]);
exports.AgentIdentitySchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    name: zod_1.z.string().min(1).max(100),
    description: zod_1.z.string().max(500).default(""),
    clientId: zod_1.z.string(),
    ownerUserId: zod_1.z.string(),
    redirectUris: zod_1.z.array(zod_1.z.string().url()).default([]),
    allowedScopes: zod_1.z.array(zod_1.z.string()).default([]),
    status: exports.AgentStatusEnum.default("active"),
    /**
     * First-party system agent (fb-0005) — e.g. the hosted google-mcp connector.
     * Not user-owned; ANY user may create a delegation grant to it. Normal
     * user-registered agents are never first-party.
     */
    isFirstParty: zod_1.z.boolean().default(false),
    lastUsedAt: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.CreateAgentSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(100),
    description: zod_1.z.string().max(500).optional(),
    redirectUris: zod_1.z.array(zod_1.z.string().url()).optional(),
    allowedScopes: zod_1.z.array(zod_1.z.string()).optional(),
});
exports.UpdateAgentSchema = zod_1.z.object({
    name: zod_1.z.string().min(1).max(100).optional(),
    description: zod_1.z.string().max(500).optional(),
    redirectUris: zod_1.z.array(zod_1.z.string().url()).optional(),
    allowedScopes: zod_1.z.array(zod_1.z.string()).optional(),
});
// ── Delegation Grant ────────────────────────────────────────────────
exports.DelegationGrantStatusEnum = zod_1.z.enum(["active", "revoked", "expired"]);
exports.DelegationConstraintsSchema = zod_1.z.object({
    expiresAt: zod_1.z.string().nullable().default(null),
    maxRequestsPerHour: zod_1.z.number().int().min(0).default(0), // 0 = unlimited
    allowedOperations: zod_1.z.array(zod_1.z.string()).default([]),
    /**
     * Ceiling on the lifetime of tokens minted against this grant, in seconds.
     * 0 = use the service default (1h).
     *
     * Raised only for agents whose delegated work legitimately outlives a session
     * — e.g. a GPU image batch still running when the user's access token expires,
     * whose results would otherwise be stranded (tdd-0060). Per-grant so one
     * long-running agent does not lengthen every other agent's tokens.
     *
     * `/v1/token/exchange` treats this as an upper bound, not a request: it takes
     * the tightest ceiling across the grants in play and further clamps to
     * `expiresAt`, so a token can never outlive the delegation authorising it.
     */
    maxTokenTtlSeconds: zod_1.z.number().int().min(0).max(7 * 24 * 3600).default(0),
});
exports.DelegationGrantSchema = zod_1.z.object({
    id: zod_1.z.string().uuid(),
    userId: zod_1.z.string(),
    agentId: zod_1.z.string(),
    provider: zod_1.z.string(),
    allowedScopes: zod_1.z.array(zod_1.z.string()),
    constraints: exports.DelegationConstraintsSchema,
    status: exports.DelegationGrantStatusEnum.default("active"),
    lastUsedAt: zod_1.z.string().optional(),
    createdAt: zod_1.z.string(),
    updatedAt: zod_1.z.string(),
});
exports.CreateDelegationSchema = zod_1.z.object({
    agentId: zod_1.z.string(),
    provider: zod_1.z.string(),
    allowedScopes: zod_1.z.array(zod_1.z.string()).min(1),
    constraints: exports.DelegationConstraintsSchema.optional(),
});
// ── Provider Token Vault (types only, no tokens exposed) ────────────
exports.VaultProviderInfoSchema = zod_1.z.object({
    provider: zod_1.z.string(),
    providerAccountId: zod_1.z.string().optional(),
    scopes: zod_1.z.array(zod_1.z.string()),
    connectedAt: zod_1.z.string(),
    lastRefreshedAt: zod_1.z.string().optional(),
});
exports.VaultConnectSchema = zod_1.z.object({
    provider: zod_1.z.string(),
    accessToken: zod_1.z.string(),
    refreshToken: zod_1.z.string(),
    expiresIn: zod_1.z.number().int().optional(), // seconds until access token expires
    scopes: zod_1.z.array(zod_1.z.string()),
    providerAccountId: zod_1.z.string().optional(),
});
// ── Token Exchange (RFC 8693) ───────────────────────────────────────
exports.TokenExchangeRequestSchema = zod_1.z.object({
    grant_type: zod_1.z.literal("urn:ietf:params:oauth:grant-type:token-exchange"),
    subject_token: zod_1.z.string(),
    subject_token_type: zod_1.z.literal("urn:ietf:params:oauth:token-type:jwt"),
    requested_token_type: zod_1.z.literal("urn:ietf:params:oauth:token-type:access_token").optional(),
    scope: zod_1.z.string().optional(), // space-separated scopes
    client_id: zod_1.z.string(),
    client_secret: zod_1.z.string(),
});
exports.TokenExchangeResponseSchema = zod_1.z.object({
    access_token: zod_1.z.string(),
    issued_token_type: zod_1.z.literal("urn:ietf:params:oauth:token-type:access_token"),
    token_type: zod_1.z.literal("Bearer"),
    expires_in: zod_1.z.number().int(),
});
